import { getLocalParticipant, getParticipantById, getRemoteParticipants, getVirtualScreenshareParticipantOwnerId, isScreenShareParticipant } from '../base/participants/functions';
import { toState } from '../base/redux/functions';
import { ACCOUNT_IDENTITY_PROPERTY, MEDIA_UPLINK_ACCOUNT_SEPARATOR, MEDIA_UPLINK_CLAIM_SEPARATOR, MEDIA_UPLINK_OWNER_PROPERTY, MULTI_DEVICE_MODE_SINGLE } from './constants';
/**
 * 编码上行抢占声明（内嵌 accountId + 时间戳，避免身份属性未到齐时无法匹配兄弟端）。
 *
 * @param {string} accountId - 门户账号 id.
 * @param {string} endpointId - 本端 endpoint id.
 * @param {number} timestampMs - 抢占时间戳.
 * @returns {string}
 */
export function encodeUplinkClaim(accountId, endpointId, timestampMs = Date.now()) {
    return `${accountId}${MEDIA_UPLINK_ACCOUNT_SEPARATOR}${endpointId}${MEDIA_UPLINK_CLAIM_SEPARATOR}${timestampMs}`;
}
/**
 * 解析上行抢占声明。
 * 兼容：accountId|endpointId@ts / endpointId@ts / endpointId
 *
 * @param {string|undefined} raw - Presence 原始值.
 * @returns {IUplinkClaim|undefined}
 */
export function parseUplinkClaim(raw) {
    if (!raw || typeof raw !== 'string') {
        return undefined;
    }
    let accountId;
    let rest = raw;
    const acctSep = raw.indexOf(MEDIA_UPLINK_ACCOUNT_SEPARATOR);
    if (acctSep > 0) {
        accountId = raw.slice(0, acctSep);
        rest = raw.slice(acctSep + 1);
    }
    const sep = rest.lastIndexOf(MEDIA_UPLINK_CLAIM_SEPARATOR);
    if (sep <= 0) {
        return {
            accountId,
            endpointId: rest,
            ts: 0
        };
    }
    const endpointId = rest.slice(0, sep);
    const ts = Number(rest.slice(sep + 1));
    if (!endpointId || !Number.isFinite(ts)) {
        return {
            accountId,
            endpointId: rest,
            ts: 0
        };
    }
    return {
        accountId,
        endpointId,
        ts
    };
}
/**
 * 读取参会者 Presence 上的上行主设备声明原文。
 *
 * @param {IParticipant|undefined} participant - 参会者.
 * @returns {string|undefined}
 */
function readUplinkOwnerClaimRaw(participant) {
    if (!participant) {
        return undefined;
    }
    const claim = participant.mediaUplinkOwner
        || participant[MEDIA_UPLINK_OWNER_PROPERTY];
    return typeof claim === 'string' && claim ? claim : undefined;
}
/**
 * 读取参会者的账号身份 id（JWT / Presence / 上行声明内嵌）。
 *
 * @param {IParticipant|undefined} participant - 参会者.
 * @returns {string|undefined}
 */
export function getAccountIdentityId(participant) {
    if (!participant) {
        return undefined;
    }
    const fromPresence = participant.accountIdentityId
        || participant[ACCOUNT_IDENTITY_PROPERTY];
    if (typeof fromPresence === 'string' && fromPresence) {
        return fromPresence;
    }
    const fromContext = participant.userContext?.id;
    if (fromContext) {
        return String(fromContext);
    }
    if (participant.jwtId) {
        return String(participant.jwtId);
    }
    // 身份属性尚未同步时，从上行声明内嵌的 accountId 回退
    const fromClaim = parseUplinkClaim(readUplinkOwnerClaimRaw(participant))?.accountId;
    if (fromClaim) {
        return fromClaim;
    }
    return undefined;
}
/**
 * 当前会议是否处于「可多设备」模式（默认 MULTI）。
 *
 * @param {IStateful} stateful - redux state.
 * @returns {boolean}
 */
export function isMultiDeviceModeEnabled(stateful) {
    const state = toState(stateful);
    const mode = state['features/base/config']?.multiDeviceMode;
    if (mode === MULTI_DEVICE_MODE_SINGLE) {
        return false;
    }
    // 未配置或 MULTI：多端同开
    return mode !== MULTI_DEVICE_MODE_SINGLE;
}
/**
 * 在同账号设备中选出最新上行主设备（按声明时间戳，后开抢占）。
 * 账号匹配优先用声明内嵌 accountId，避免 Presence 身份属性竞态。
 *
 * @param {IReduxState} state - state.
 * @param {string} accountId - 账号 id.
 * @returns {IUplinkClaim|undefined}
 */
export function getWinningUplinkClaim(state, accountId) {
    let best;
    const consider = (participant) => {
        if (!participant?.id) {
            return;
        }
        const parsed = parseUplinkClaim(readUplinkOwnerClaimRaw(participant));
        if (!parsed) {
            return;
        }
        const claimAccountId = parsed.accountId || getAccountIdentityId(participant);
        if (claimAccountId !== accountId) {
            return;
        }
        // 仅统计「自称」：声明的 endpoint 必须是自己
        if (parsed.endpointId !== participant.id) {
            return;
        }
        if (!best || parsed.ts >= best.ts) {
            best = {
                accountId: claimAccountId,
                endpointId: parsed.endpointId,
                ts: parsed.ts
            };
        }
    };
    consider(getLocalParticipant(state));
    getRemoteParticipants(state).forEach(p => consider(p));
    return best;
}
/**
 * 解析同账号组的「主」endpoint：优先本端、再 Presence uplinkOwner、再首个远端。
 *
 * @param {IReduxState} state - state.
 * @param {string} accountId - 账号 id.
 * @returns {string|undefined} endpoint id
 */
export function getPrimaryEndpointIdForAccount(state, accountId) {
    const local = getLocalParticipant(state);
    const localAccountId = getAccountIdentityId(local);
    if (local?.id && localAccountId === accountId) {
        return local.id;
    }
    const remotes = getRemoteParticipants(state);
    const siblings = [];
    remotes.forEach(participant => {
        if (getAccountIdentityId(participant) === accountId) {
            siblings.push(participant);
        }
    });
    const winning = getWinningUplinkClaim(state, accountId);
    if (winning && (siblings.some(s => s.id === winning.endpointId) || local?.id === winning.endpointId)) {
        return winning.endpointId;
    }
    // 优先有视频/屏幕共享的端
    const withVideo = siblings.find(s => {
        const sources = s.sources;
        return sources && sources.size > 0;
    });
    if (withVideo) {
        return withVideo.id;
    }
    return siblings[0]?.id;
}
/**
 * 远端参会者是否应在宫格/列表中隐藏（同账号次端）。
 *
 * @param {IStateful} stateful - state.
 * @param {string} participantId - endpoint id.
 * @returns {boolean}
 */
export function isHiddenDuplicateSibling(stateful, participantId) {
    const state = toState(stateful);
    if (!isMultiDeviceModeEnabled(state)) {
        return false;
    }
    const participant = getParticipantById(state, participantId);
    if (!participant || participant.local) {
        return false;
    }
    // 虚拟屏幕共享轨：按归属 endpoint 判断是否为同账号次端
    if (participant.fakeParticipant) {
        if (!isScreenShareParticipant(participant)) {
            return false;
        }
        const ownerId = getVirtualScreenshareParticipantOwnerId(participantId);
        if (!ownerId || ownerId === participantId) {
            return false;
        }
        // 本地自己的屏幕共享不隐藏
        const local = getLocalParticipant(state);
        if (local?.id && ownerId === local.id) {
            return false;
        }
        return isHiddenDuplicateSibling(state, ownerId);
    }
    const accountId = getAccountIdentityId(participant);
    const local = getLocalParticipant(state);
    const localAccountId = getAccountIdentityId(local);
    if (!accountId) {
        return false;
    }
    // 本机看到「自己的另一台设备」：整组隐藏远端兄弟（本端已代表该账号）
    if (localAccountId === accountId) {
        return true;
    }
    const primaryId = getPrimaryEndpointIdForAccount(state, accountId);
    const hidden = Boolean(primaryId && primaryId !== participantId);
    return hidden;
}
/**
 * 过滤掉同账号次端后的远端 id 列表。
 *
 * @param {IStateful} stateful - state.
 * @param {Array<string>} participantIds - 原始 id 列表.
 * @returns {Array<string>}
 */
export function filterDuplicateSiblingIds(stateful, participantIds) {
    if (!isMultiDeviceModeEnabled(stateful)) {
        return participantIds;
    }
    const filtered = participantIds.filter(id => !isHiddenDuplicateSibling(stateful, id));
    return filtered;
}
/**
 * 本地是否为当前账号的上行主设备。
 * 后开抢占：同账号各端自称中，时间戳最新者胜出。
 *
 * @param {IStateful} stateful - state.
 * @returns {boolean}
 */
export function isLocalMediaUplinkOwner(stateful) {
    const state = toState(stateful);
    const local = getLocalParticipant(state);
    if (!local?.id) {
        return true;
    }
    if (!isMultiDeviceModeEnabled(state)) {
        return true;
    }
    const accountId = getAccountIdentityId(local);
    if (!accountId) {
        return true;
    }
    const winning = getWinningUplinkClaim(state, accountId);
    if (winning) {
        return winning.endpointId === local.id;
    }
    // 无人声明时：本端默认可上行（首台设备）
    return true;
}
/**
 * 是否存在同账号其它设备。
 *
 * @param {IStateful} stateful - state.
 * @returns {boolean}
 */
export function hasSiblingDevices(stateful) {
    const state = toState(stateful);
    const local = getLocalParticipant(state);
    const accountId = getAccountIdentityId(local);
    if (!accountId) {
        return false;
    }
    let count = 0;
    getRemoteParticipants(state).forEach(p => {
        if (getAccountIdentityId(p) === accountId) {
            count += 1;
        }
    });
    return count > 0;
}
